import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  ListChecks, CheckCircle2, AlertTriangle, Clock, Paperclip, Play,
  Upload, FileText, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

export default function ChecklistActivity() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [score, setScore] = useState(null);
  const [submitFor, setSubmitFor] = useState(null);
  const [submitNote, setSubmitNote] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data }, sc] = await Promise.all([
        api.get("/checklist/instances/mine", {
          params: { include_completed: includeCompleted },
        }),
        api.get("/checklist/scorecard").catch(() => ({ data: null })),
      ]);
      setRows(data || []);
      setScore(sc.data || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [includeCompleted]);

  const onStart = async (id) => {
    try {
      await api.patch(`/checklist/instances/${id}/status`, { status: "in_progress" });
      toast.success("Started");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    }
  };

  const onAttach = async (id, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/checklist/instances/${id}/attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Attached");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Attach failed");
    }
  };

  const onComplete = async (id, note) => {
    try {
      await api.post(`/checklist/instances/${id}/submit`, { note: note || null });
      toast.success("Marked as done — submitted for approval");
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(formatApiErrorDetail(detail) || "Could not submit");
    }
  };

  return (
    <div data-testid="checklist-activity-page" className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <ListChecks className="h-7 w-7 text-red-600 mt-1" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase 6 · My Tasks</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Checklist Activity
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {user?.name ? `Hi ${user.name}, ` : ""}these are the recurring compliance tasks
            currently in your bucket. Mark them done by the effective due date — your
            submission is then sent to MIS / Senior Management for approval.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Switch checked={includeCompleted}
                  onCheckedChange={setIncludeCompleted}
                  data-testid="toggle-include-completed" />
          <Label className="text-xs">Show completed</Label>
        </div>
      </div>

      {score && (
        <ScoreCard s={score} userId={user?.id} />
      )}

      <div className="space-y-2">
        {!loading && rows.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-lg p-8 text-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-300 mx-auto" />
            <h3 className="mt-2 text-sm font-semibold text-slate-700">All clear!</h3>
            <p className="text-xs text-slate-500">No checklist items in your bucket right now.</p>
          </div>
        )}
        {rows.map((r) => (
          <InstanceCard key={r.id} r={r}
                        onStart={() => onStart(r.id)}
                        onAttach={(f) => onAttach(r.id, f)}
                        onComplete={() => { setSubmitFor(r); setSubmitNote(""); }} />
        ))}
      </div>

      {/* Mark-as-Done dialog with optional comment + attachment hint */}
      <Dialog open={!!submitFor} onOpenChange={(o) => !o && setSubmitFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="submit-dialog">
          <DialogHeader>
            <DialogTitle>Mark as Done — {submitFor?.template_title}</DialogTitle>
            <DialogDescription>
              This will be sent to <b>MIS / Senior Management</b> for approval.
              Add a short comment for the approver if helpful (e.g. "PF challan
              Rs 1.42L paid via UPI on 7th").
            </DialogDescription>
          </DialogHeader>
          {submitFor?.attachment_required && !((submitFor?.attachment_urls || []).length) && (
            <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-900 p-2 rounded">
              ⚠ Proof attachment is required for this task. Please cancel, attach a file, then mark as done.
            </div>
          )}
          {((submitFor?.attachment_urls || []).length > 0) && (
            <div className="text-[11px] text-slate-600">
              <Paperclip className="inline h-3 w-3 mr-1" />
              {submitFor.attachment_urls.length} file{submitFor.attachment_urls.length > 1 ? "s" : ""} attached
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">Comment (optional)</Label>
            <Textarea rows={3} value={submitNote}
                      onChange={(e) => setSubmitNote(e.target.value)}
                      data-testid="submit-note-input"
                      placeholder="Anything the approver should know?" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSubmitFor(null)}>Cancel</Button>
            <Button className="brand-bg brand-bg-hover text-white"
                    data-testid="submit-confirm-btn"
                    onClick={() => {
                      const id = submitFor?.id;
                      onComplete(id, submitNote);
                      setSubmitFor(null);
                    }}>
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Confirm Mark as Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScoreCard({ s, userId }) {
  const c = s.counts || {};
  const total = (c.on_time || 0) + (c.late || 0) + (c.missed || 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-2 md:grid-cols-5 gap-3"
         data-testid={`scorecard-${s.user_id || userId}`}>
      <Stat label="On time" value={c.on_time || 0} tone="green" />
      <Stat label="Late" value={c.late || 0} tone="amber" />
      <Stat label="Missed" value={c.missed || 0} tone="red" />
      <Stat label="Open" value={c.open || 0} tone="slate" />
      <div className="border-l border-slate-100 pl-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">Net negative</div>
        <div className={`text-2xl font-semibold ${s.net_negative_marks < 0 ? "text-red-600" : "text-emerald-600"}`}>
          {s.net_negative_marks}
        </div>
        <div className="text-[10px] text-slate-400">Across {total} evaluated</div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = {
    green: "text-emerald-700",
    amber: "text-amber-700",
    red: "text-red-700",
    slate: "text-slate-700",
  }[tone];
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-2xl font-semibold ${cls}`}>{value}</div>
    </div>
  );
}

function InstanceCard({ r, onStart, onAttach, onComplete }) {
  const tone = r.overdue ? "red"
    : r.status === "completed" ? "green"
    : r.status === "pending_approval" ? "violet"
    : r.status === "in_progress" ? "amber" : "slate";
  const borderCls = {
    red: "border-red-300",
    green: "border-emerald-300",
    violet: "border-violet-300",
    amber: "border-amber-300",
    slate: "border-slate-200",
  }[tone];
  const completed = r.status === "completed";
  const pendingApproval = r.status === "pending_approval";
  const attCount = (r.attachment_urls || []).length;
  return (
    <div className={`bg-white border-2 ${borderCls} rounded-lg p-4`}
         data-testid={`activity-row-${r.id}`}>
      <div className="flex items-start gap-3">
        <Indicator inst={r} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-slate-900 text-sm">{r.template_title}</h3>
            <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{r.category}</span>
            <span className="text-[10px] font-mono text-slate-500">{r.period_label}</span>
            {r.attachment_required && (
              <span className="text-[10px] bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                Proof required
              </span>
            )}
            {pendingApproval && (
              <span className="text-[10px] bg-violet-100 text-violet-800 px-1.5 py-0.5 rounded font-medium">
                Awaiting approval
              </span>
            )}
          </div>
          <div className="text-xs text-slate-600 mt-1">
            Due (effective): <b>{r.effective_due_date}</b>
            {r.nominal_due_date !== r.effective_due_date && (
              <span className="text-slate-400 ml-1">(rolled back from {r.nominal_due_date})</span>
            )}
            {r.deadline_with_grace && r.grace_days > 0 && (
              <span className="text-slate-400 ml-1">· grace until {r.deadline_with_grace}</span>
            )}
          </div>
          {pendingApproval && r.submitted_at && (
            <div className="text-[11px] text-violet-700 mt-1">
              Submitted on {new Date(r.submitted_at).toLocaleString()} — waiting for MIS / Senior Mgmt
            </div>
          )}
          {r.submission_note && (
            <div className="text-[11px] text-slate-700 mt-1.5 bg-slate-50 border border-slate-200 rounded px-2 py-1">
              <MessageSquare className="inline h-3 w-3 mr-1 text-slate-400" />
              <b>Your comment:</b> {r.submission_note}
            </div>
          )}
          {r.approval_note && completed && (
            <div className="text-[11px] text-emerald-800 mt-1.5 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
              <MessageSquare className="inline h-3 w-3 mr-1" />
              <b>Approver note:</b> {r.approval_note}
            </div>
          )}
          {r.rejection_reason && r.status === "in_progress" && (
            <div className="text-[11px] text-red-700 mt-1 bg-red-50 border border-red-200 rounded px-2 py-1">
              <b>Sent back:</b> {r.rejection_reason}
            </div>
          )}
          {attCount > 0 && (
            <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
              <Paperclip className="h-3 w-3" /> {attCount} attachment{attCount > 1 ? "s" : ""}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {!completed && !pendingApproval && (
            <>
              {r.status === "not_started" && (
                <Button size="sm" variant="outline"
                        onClick={onStart}
                        data-testid={`start-btn-${r.id}`}>
                  <Play className="h-3 w-3 mr-1" /> Start
                </Button>
              )}
              <label className="cursor-pointer">
                <input type="file" className="hidden"
                       data-testid={`attach-input-${r.id}`}
                       onChange={(e) => onAttach(e.target.files?.[0])} />
                <span className="inline-flex items-center px-2.5 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50">
                  <Upload className="h-3 w-3 mr-1" /> Attach
                </span>
              </label>
              <Button size="sm" className="brand-bg brand-bg-hover text-white"
                      onClick={onComplete}
                      data-testid={`complete-btn-${r.id}`}>
                <CheckCircle2 className="h-3 w-3 mr-1" /> Mark as Done
              </Button>
            </>
          )}
          {pendingApproval && (
            <span className="text-[11px] text-violet-700 inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> Pending approval
            </span>
          )}
          {completed && (
            <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> Done
              {r.approved_at && (
                <span className="text-slate-400">
                  · approved {new Date(r.approved_at).toLocaleDateString()}
                  {r.approved_by_name ? ` by ${r.approved_by_name}` : ""}
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Indicator({ inst }) {
  if (inst.status === "completed") return <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />;
  if (inst.status === "pending_approval") return <Clock className="h-5 w-5 text-violet-500 mt-0.5" />;
  if (inst.overdue || inst.timeliness === "missed") return <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />;
  if (inst.status === "in_progress") return <Clock className="h-5 w-5 text-amber-500 mt-0.5" />;
  return <FileText className="h-5 w-5 text-slate-400 mt-0.5" />;
}
